import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Loader2, Hammer, Sparkles, Quote, ArrowRight, RotateCcw, Layers, Footprints } from "lucide-react";
import { toast } from "sonner";

export default function Onboarding() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const ripeti = params.get("ripeti") === "1";
  const { user, refreshUser } = useAuth();

  const [quiz, setQuiz] = useState(null);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [confirmRipeti, setConfirmRipeti] = useState(ripeti && !!user?.maestro_guida);

  useEffect(() => {
    sessionStorage.removeItem("bottega_onboarding");
    api.get("/onboarding/quiz").then((r) => setQuiz(r.data.domande)).catch(() => setQuiz([]));
  }, []);

  const scegli = async (optId) => {
    const q = quiz[step];
    const next = { ...answers, [q.id]: optId };
    setAnswers(next);
    if (step < quiz.length - 1) {
      setStep(step + 1);
    } else {
      setBusy(true);
      try {
        const { data } = await api.post("/onboarding/submit", { answers: next });
        setResult(data);
        await refreshUser();
      } catch {
        toast.error("Qualcosa è andato storto. Riprova.");
      } finally {
        setBusy(false);
      }
    }
  };

  const salta = async () => {
    try { await api.post("/onboarding/skip"); } catch { /* ignore */ }
    navigate("/");
  };

  const chooseMode = async (m) => {
    setBusy(true);
    try {
      await api.post("/me/modalita", { modalita: m });
      await refreshUser();
      toast.success(m === "mathema" ? "Modalità Máthema attivata" : "Modalità Compendium attivata");
    } catch {
      toast.error("Non è stato possibile salvare la modalità.");
    } finally {
      setBusy(false);
    }
  };

  const needMode = result && user?.tier === "normale" && !user?.modalita;

  if (!quiz)
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" strokeWidth={1.5} />
      </div>
    );

  // Conferma prima di sostituire il Maestro attuale (ripetizione dal profilo)
  if (confirmRipeti)
    return (
      <div className="min-h-screen flex items-center justify-center px-5">
        <div className="max-w-md w-full bg-card border border-border rounded-lg p-8 text-center" data-testid="onboarding-confirm-ripeti">
          <RotateCcw className="h-10 w-10 mx-auto text-primary mb-4" strokeWidth={1.5} />
          <h1 className="font-serif text-2xl font-bold mb-2">Rifare il test?</h1>
          <p className="text-muted-foreground mb-6">
            Se completi di nuovo il test, il tuo Maestro-guida attuale verrà sostituito dal nuovo risultato.
          </p>
          <div className="flex gap-3 justify-center">
            <Button variant="outline" className="rounded-sm" onClick={() => navigate("/account")} data-testid="ripeti-cancel-btn">
              Annulla
            </Button>
            <Button className="rounded-sm bg-primary hover:bg-primary/90" onClick={() => setConfirmRipeti(false)} data-testid="ripeti-confirm-btn">
              Sì, ricomincia
            </Button>
          </div>
        </div>
      </div>
    );

  // Schermata risultato
  if (result)
    return (
      <div className="min-h-screen flex items-center justify-center px-5 py-12 bg-gradient-to-b from-primary/[0.04] to-transparent">
        <div className="max-w-lg w-full text-center" data-testid="onboarding-result">
          <p className="text-sm uppercase tracking-widest text-primary font-semibold mb-2 flex items-center justify-center gap-2">
            <Sparkles className="h-4 w-4" strokeWidth={1.5} /> Il tuo Maestro-guida
          </p>
          <h1 className="font-serif text-5xl font-bold mb-3" data-testid="result-maestro">{result.maestro}</h1>
          <div className="bg-card border border-border rounded-lg p-6 my-6 text-left">
            <Quote className="h-6 w-6 text-primary/50 mb-2" strokeWidth={1.5} />
            <p className="font-serif text-lg italic mb-2">«{result.citazione}»</p>
            <p className="text-xs text-muted-foreground mb-4">— {result.citazione_autore}</p>
            <p className="text-muted-foreground leading-relaxed">{result.benvenuto}</p>
          </div>

          {needMode ? (
            <div data-testid="onboarding-mode-choice" className="text-left">
              <p className="text-sm uppercase tracking-widest text-primary font-semibold mb-1 text-center">Come vuoi studiare?</p>
              <p className="text-sm text-muted-foreground mb-5 text-center">Puoi cambiare idea quando vuoi da «Il tuo posto».</p>
              <div className="grid sm:grid-cols-2 gap-3">
                <button onClick={() => chooseMode("compendium")} disabled={busy} data-testid="mode-compendium-btn"
                  className="text-left p-5 rounded-lg border border-border bg-card hover:border-primary hover:bg-primary/[0.04] transition-all hover:-translate-y-0.5 disabled:opacity-60">
                  <Layers className="h-6 w-6 text-primary mb-2" strokeWidth={1.5} />
                  <p className="font-serif text-lg font-bold">Compendium</p>
                  <p className="text-sm text-muted-foreground mt-1">Tutte le lectiones sbloccate da subito. Studi con la massima libertà, nel tuo ordine.</p>
                </button>
                <button onClick={() => chooseMode("mathema")} disabled={busy} data-testid="mode-mathema-btn"
                  className="text-left p-5 rounded-lg border border-border bg-card hover:border-primary hover:bg-primary/[0.04] transition-all hover:-translate-y-0.5 disabled:opacity-60">
                  <Footprints className="h-6 w-6 text-primary mb-2" strokeWidth={1.5} />
                  <p className="font-serif text-lg font-bold">Máthema</p>
                  <p className="text-sm text-muted-foreground mt-1">Sblocco a tappe: superi la Prova di comprensione (max 2 al giorno) per accedere alla lectio successiva.</p>
                </button>
              </div>
              {busy && <div className="py-4 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" strokeWidth={1.5} /></div>}
            </div>
          ) : (
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button className="rounded-sm bg-primary hover:bg-primary/90" data-testid="result-cta-course"
              onClick={() => navigate(`/corso/${result.course_id}`)}>
              Inizia con {result.maestro} <ArrowRight className="h-4 w-4 ml-1.5" strokeWidth={1.5} />
            </Button>
            <Button variant="outline" className="rounded-sm" data-testid="result-go-dashboard"
              onClick={() => navigate("/")}>
              Esplora tutta la Bottega
            </Button>
          </div>
          )}
          {!needMode && (
          <p className="text-xs text-muted-foreground mt-4">Il corso «{result.corso_nome}» è il tuo punto di partenza suggerito.</p>
          )}
        </div>
      </div>
    );

  // Quiz
  const q = quiz[step];
  const pct = Math.round((step / quiz.length) * 100);
  return (
    <div className="min-h-screen flex flex-col">
      <div className="flex items-center justify-between px-5 sm:px-10 py-5 border-b border-border">
        <div className="flex items-center gap-2.5">
          <Hammer className="h-6 w-6 text-primary" strokeWidth={1.5} />
          <span className="font-serif text-lg font-bold">Scopri il tuo Maestro</span>
        </div>
        <button onClick={salta} data-testid="onboarding-skip-btn"
          className="text-sm text-muted-foreground hover:text-foreground transition-colors">
          Salta, scegli dopo
        </button>
      </div>

      <div className="flex-1 flex items-center justify-center px-5 py-10">
        <div className="max-w-xl w-full">
          <div className="flex items-center gap-3 mb-8">
            <Progress value={pct} className="h-1.5" />
            <span className="text-xs text-muted-foreground tabular-nums whitespace-nowrap">{step + 1}/{quiz.length}</span>
          </div>

          <h1 className="font-serif text-3xl sm:text-4xl font-bold mb-8 leading-tight" data-testid="onboarding-question">
            {q.testo}
          </h1>

          {busy ? (
            <div className="py-10 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" strokeWidth={1.5} /></div>
          ) : (
            <div className="grid gap-3">
              {q.opzioni.map((opt) => (
                <button
                  key={opt.id}
                  onClick={() => scegli(opt.id)}
                  data-testid={`onboarding-option-${q.id}-${opt.id}`}
                  className="text-left p-4 rounded-md border border-border bg-card hover:border-primary hover:bg-primary/[0.04] transition-all hover:-translate-y-0.5"
                >
                  <span className="font-medium">{opt.testo}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
